// ============================================================
// GSTExcelExport.jsx
// Drop-in component for Ordermanagement.jsx
// ============================================================
//
// HOW TO USE:
//   1. import GSTExcelExport from "./GSTExcelExport";
//   2. Remove old generateExcel fn + old 📊 Export button
//   3. Add in JSX (after your print buttons row):
//        <GSTExcelExport startDate={startDate} endDate={endDate} search={search} />
//
// ============================================================

import React, { useState } from "react";
import * as XLSX from "xlsx";
import { getApiUrl } from "../utils/api";
import { toast } from "react-toastify";

// Auto financial year:
//   April–March = this year → next (e.g. 2025-26)
//   Jan–March   = last year → this (e.g. 2024-25)
const getFinancialYear = () => {
  const now   = new Date();
  const month = now.getMonth(); // 0=Jan … 3=Apr
  const year  = now.getFullYear();
  if (month >= 3) return `${year}-${String(year + 1).slice(2)}`;
  return `${year - 1}-${String(year).slice(2)}`;
};

const GSTExcelExport = ({ startDate, endDate, search }) => {
  const [companyName,   setCompanyName]   = useState("BSK BOUTIQUE");
  const [gstin,         setGstin]         = useState("33MXEPK1134N1Z9");
  const [hsnCode,       setHsnCode]       = useState("62041200");
  const [gstRateInput,  setGstRateInput]  = useState("12");   // user types 5, 12, 18 …
  const [supplierId,    setSupplierId]    = useState("");
  const [loading,       setLoading]       = useState(false);

  const financialYear  = getFinancialYear();                   // auto, read-only
  const effectiveRate  = parseFloat(gstRateInput) / 100 || 0; // "12" → 0.12

  const formatDisplay = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN") : "";

  // ── Main export ─────────────────────────────────────────
  const handleExport = async () => {
    if (!startDate || !endDate)
      return toast.warning("Please select From and To dates before exporting");

    if (!gstRateInput || isNaN(parseFloat(gstRateInput)))
      return toast.warning("Please enter a valid GST rate (e.g. 5, 12, 18)");

    setLoading(true);
    toast.info("Fetching orders…");

    try {
      const res = await fetch(getApiUrl("order-range"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from: 1,
          to: 999999,
          search,
          startDate,
          endDate,
        }),
      });
      const data = await res.json();

      if (!data.success || !data.data.length)
        return toast.warning("No orders found for selected date range");

      const allOrders = data.data;
      const rate      = effectiveRate;

      // Header — matches Format.xlsx exactly
      const header = [
        "company_name",
        "gstin",
        "customer name",
        "sub_order_num",
        "order_date",
        "hsn_code",
        "quantity",
        "gst_rate",
        "total_taxable_sale_value",
        "tax_amount",
        "total_invoice_value",
        "taxable_shipping",
        "end_customer_state_new",
        "financial_year",
        "supplier_id",
      ];

      const rows = [header];

      allOrders.forEach((order) => {
        const totalInvoice = Number(order.amount) || 0;

        // GST-inclusive breakdown:
        // taxable = invoice ÷ (1 + rate)  |  tax = invoice − taxable
        const taxableValue = parseFloat((totalInvoice / (1 + rate)).toFixed(2));
        const taxAmount    = parseFloat((totalInvoice - taxableValue).toFixed(2));
        const totalQty     = order.products.reduce((s, p) => s + (p.qty || 0), 0);
        const orderDate    = order.createdAt
          ? new Date(order.createdAt).toLocaleDateString("en-IN")
          : "";
        const subOrderNum  = order.orderUnique || order.orderReference || "";

        rows.push([
          companyName,                        // company_name
          gstin,                              // gstin
          order.userId?.name || "Guest",      // customer name
          subOrderNum,                        // sub_order_num
          orderDate,                          // order_date
          hsnCode,                            // hsn_code
          totalQty,                           // quantity
          rate,                               // gst_rate  (decimal: 0.12)
          taxableValue,                       // total_taxable_sale_value
          taxAmount,                          // tax_amount
          totalInvoice,                       // total_invoice_value
          "",                                 // taxable_shipping (blank)
          order.address?.state || "",         // end_customer_state_new
          financialYear,                      // financial_year  (auto)
          supplierId,                         // supplier_id
        ]);
      });

      // Build & download workbook
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = header.map((_, ci) => ({
        wch: Math.min(40, Math.max(12, ...rows.map((r) => String(r[ci] ?? "").length))),
      }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sales Data");
      XLSX.writeFile(wb, `GST_Sales_${startDate}_to_${endDate}.xlsx`);
      toast.success(`✅ ${allOrders.length} orders exported!`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export");
    } finally {
      setLoading(false);
    }
  };

  // ── Styles ──────────────────────────────────────────────
  const s = {
    card: {
      background: "#fffbeb",
      border: "1.5px solid #fbbf24",
      borderRadius: "10px",
      padding: "16px 20px",
      marginBottom: "16px",
    },
    lbl: {
      fontSize: "11px",
      fontWeight: 700,
      color: "#92400e",
      marginBottom: "3px",
      display: "block",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    inp: {
      fontSize: "13px",
      padding: "6px 9px",
      borderRadius: "6px",
      border: "1px solid #d97706",
      background: "#fff",
      width: "100%",
      outline: "none",
    },
    readOnly: {
      fontSize: "13px",
      padding: "6px 9px",
      borderRadius: "6px",
      border: "1px solid #e5e7eb",
      background: "#f3f4f6",
      color: "#374151",
      fontWeight: 700,
      width: "100%",
      cursor: "not-allowed",
    },
    fyBadge: {
      display: "inline-block",
      background: "#d1fae5",
      color: "#065f46",
      padding: "2px 10px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 700,
      border: "1px solid #6ee7b7",
      marginLeft: "8px",
    },
    exportBtn: (disabled) => ({
      padding: "9px 24px",
      borderRadius: "8px",
      border: "none",
      background: disabled ? "#d1d5db" : "#f59e0b",
      color: disabled ? "#9ca3af" : "#fff",
      fontWeight: 700,
      fontSize: "14px",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    }),
  };

  const isDisabled = loading || !startDate || !endDate;

  return (
    <div style={s.card}>

      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "17px" }}>📊</span>
        <strong style={{ fontSize: "15px", color: "#78350f" }}>GST Excel Export</strong>
        <span style={s.fyBadge}>FY {financialYear} · Auto ✓</span>
      </div>

      {/* Fields grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: "12px",
        marginBottom: "14px",
      }}>

        {/* Company Name */}
        <div>
          <span style={s.lbl}>Company Name</span>
          <input
            style={s.inp}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>

        {/* GSTIN */}
        <div>
          <span style={s.lbl}>GSTIN</span>
          <input
            style={s.inp}
            value={gstin}
            maxLength={15}
            placeholder="33XXXXXXXXX1Z9"
            onChange={(e) => setGstin(e.target.value.toUpperCase())}
          />
        </div>

        {/* HSN Code */}
        <div>
          <span style={s.lbl}>HSN Code</span>
          <input
            style={s.inp}
            value={hsnCode}
            placeholder="62041200"
            onChange={(e) => setHsnCode(e.target.value)}
          />
        </div>

        {/* GST Rate — plain text input */}
        <div>
          <span style={s.lbl}>GST Rate (%)</span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <input
              style={{ ...s.inp, width: "80px", fontWeight: 700, fontSize: "15px" }}
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={gstRateInput}
              placeholder="12"
              onChange={(e) => setGstRateInput(e.target.value)}
            />
            <span style={{ fontWeight: 800, color: "#d97706", fontSize: "17px" }}>%</span>
          </div>
          {gstRateInput && !isNaN(parseFloat(gstRateInput)) && (
            <div style={{ fontSize: "10px", color: "#6b7280", marginTop: "3px" }}>
              Saved in Excel as <strong>{effectiveRate.toFixed(4)}</strong>
            </div>
          )}
        </div>

        {/* Financial Year — auto, locked */}
        <div>
          <span style={s.lbl}>Financial Year</span>
          <div style={s.readOnly}>{financialYear}</div>
          <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "3px" }}>
            Changes automatically every April 1
          </div>
        </div>

        {/* Supplier ID */}
        <div>
          <span style={s.lbl}>
            Supplier ID{" "}
            <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span>
          </span>
          <input
            style={s.inp}
            value={supplierId}
            placeholder="Leave blank if none"
            onChange={(e) => setSupplierId(e.target.value)}
          />
        </div>

      </div>

      {/* Export button */}
      <button
        onClick={handleExport}
        disabled={isDisabled}
        style={s.exportBtn(isDisabled)}
        title={!startDate || !endDate ? "Select From / To dates above first" : "Export GST Excel"}
      >
        {loading
          ? "⏳ Exporting…"
          : `📥 Export GST Excel${
              startDate && endDate
                ? ` · ${formatDisplay(startDate)} → ${formatDisplay(endDate)}`
                : " · Select date range first"
            }`}
      </button>

      {/* Column legend */}
      <div style={{ fontSize: "10px", color: "#b0b8c4", marginTop: "8px", lineHeight: 1.7 }}>
        Columns: company_name · gstin · customer name · sub_order_num · order_date ·
        hsn_code · quantity · gst_rate · total_taxable_sale_value · tax_amount ·
        total_invoice_value · taxable_shipping · end_customer_state_new · financial_year · supplier_id
      </div>

    </div>
  );
};

export default GSTExcelExport;